import union from 'lodash/union';

export const replaceValueInList = <T>(
  value: T,
  list: T[],
  condition: (value: T) => boolean,
) => {
  const valueIndex = list.findIndex(condition);

  // * Add or replace a user based on whether they exist in the chat list
  if (valueIndex > -1) {
    // * Replace user with the one from the backend
    const newList = [
      ...list.slice(0, valueIndex),
      value,
      ...list.slice(valueIndex + 1),
    ];
    return newList;
  }

  return addValueToList<T>(value, list);
};

export const addValueToList = <T>(value: T, list?: T[]) => {
  if (!list) {
    return [value];
  }

  return [...list, value];
};

export const removeValueFromList = <T>(
  condition: (value: T) => boolean,
  list?: T[],
) => {
  if (!list) {
    return [];
  }

  const valueIndex = list.findIndex(condition);

  // * Add or replace a user based on whether they exist in the chat list
  if (valueIndex > -1) {
    // * Replace user with the one from the backend
    const newList = [
      ...list.slice(0, valueIndex),
      ...list.slice(valueIndex + 1),
    ];
    return newList;
  }

  return list;
};

export const pushUnique = <T>(value: T, list?: T[]) => {
  if (!list) {
    return [value];
  }

  if (list.indexOf(value) === -1) {
    return [...list, value];
  }

  return list;
};

export const pushUniques = <T>(values: T[], list?: T[]) => union(list, values);

export const removeObjectValuesFromList = <T>(
  valueIds: Array<string>,
  list: T[],
  property: keyof T,
) => {
  if (!list) return [];
  return list.filter((listValue) => {
    const propertyValue = listValue[property];

    if (typeof propertyValue !== 'string') return true;
    return !valueIds.includes(propertyValue);
  });
};

export const replaceValuesInList = <T>(
  values: Array<T>,
  list: T[],
  property: keyof T,
) => {
  if (!list) return values;

  const replacedValues = list.map((listValue) => {
    const possibleValue = values.find(
      (value) => value[property] === listValue[property],
    );
    if (!possibleValue) return listValue;
    return possibleValue;
  });

  // Check all elements in values that don't exist on list and add them
  const restList = values.filter(
    (value) =>
      !list.some((listValue) => value[property] === listValue[property]),
  );

  // Combine the lists
  return [replacedValues, restList].flat();
};

export const mapObject = <T>(
  obj: Record<string, T>,
  mapFunction: (entry: [string, T]) => unknown,
) => Object.assign({}, ...Object.entries(obj).map(mapFunction));

type Spec = Record<
  string,
  (...args: readonly any[]) => (...args: readonly any[]) => any
>;

// * Apply spec method from ramdag
export const applySpec = <T extends Spec>(
  spec: T,
  arg: any,
): { [Key in keyof T]: ReturnType<T[Key]> } =>
  mapObject(spec, ([keyOfSpec, specFunction]) => {
    return { [keyOfSpec]: specFunction(arg) };
  });

export const turnMapToObject = (
  map: Map<string, unknown>,
): Record<string, unknown> => {
  const obj = Array.from(map).reduce<Record<string, unknown>>(
    (obj, [key, value]) => {
      obj[key] = value;
      return obj;
    },
    {},
  );
  return obj;
};

export const turnObjectToMap = (
  obj: Record<string, unknown>,
): Map<string, unknown> => {
  return new Map(Object.entries(obj));
};

export const removeObjectKey = (obj: any, key: string) => {
  const objCopy = { ...obj };
  delete objCopy[key];
  return objCopy;
};

export function callsites() {
  // eslint-disable-next-line no-underscore-dangle
  const _prepareStackTrace = Error.prepareStackTrace;
  Error.prepareStackTrace = (_, stack) => stack;
  const stack = new Error().stack?.slice(1);
  Error.prepareStackTrace = _prepareStackTrace;
  return stack;
}

export const uniqueBy = <T>(array: Array<T>, key: keyof T) => {
  const replacement: Array<T> = [];

  array.forEach((value) => {
    if (
      !replacement.find(
        (replacementValue) => replacementValue[key] === value[key],
      )
    ) {
      return replacement.push(value);
    }
  });

  return replacement;
};

export function stringToArrayBuffer(s: string) {
  const buf = new ArrayBuffer(s.length);
  const view = new Uint8Array(buf);

  // * Couldn't find a way to refactor this
  // eslint-disable-next-line no-loops/no-loops
  for (let i = 0; i !== s.length; ++i) view[i] = s.charCodeAt(i) & 0xff;

  return buf;
}

// * Transform a base64 string into a buffer
export const base64ToBuffer = (base64: string) =>
  // * Assuming the correct base64 format is used this should work for both base64:// URI scheme as well as without
  base64.includes(',')
    ? Buffer.from(base64.split(',')[1], 'base64')
    : Buffer.from(base64, 'base64');

export const getFieldFromData = function getFieldFromData(
  field: string,
  data: { [x: string]: any },
): any | string {
  if (field && field.indexOf('.') !== -1) {
    const splittedField = field.split('.');
    const firstField = splittedField.shift();

    if (!firstField) {
      return '';
    }

    if (data && typeof data?.[firstField] !== 'undefined') {
      const dataSource = data?.[firstField];
      return getFieldFromData(splittedField.join('.'), dataSource);
    }

    return '';
  }
  return typeof data[field] !== 'undefined' ? data[field] : '';
};

export const range = (low: number, high: number) =>
  low < high
    ? Array(high - low + 1)
        .fill(0)
        .map((_, index) => index + low)
    : [];

export const moveElementInArrayFromIndexToIndex = <T>(
  array?: T[] | null,
  fromIndex?: number | null,
  toIndex?: number | null,
) => {
  const valueArray = array ?? [];

  if (
    typeof fromIndex !== 'number' ||
    typeof toIndex !== 'number' ||
    fromIndex === toIndex
  ) {
    return valueArray;
  }

  const newArray = [...valueArray];

  const target = newArray[fromIndex];
  const inc = toIndex < fromIndex ? -1 : 1;

  // eslint-disable-next-line no-loops/no-loops
  for (let i = fromIndex; i !== toIndex; i += inc) {
    newArray[i] = newArray[i + inc];
  }

  newArray[toIndex] = target;

  return newArray;
};

export const supplant = (string: string, object?: Record<string, unknown>) =>
  string?.replace?.(/{([^{}]*)}/g, (match, matchKey) => {
    const objectValue = object?.[matchKey];

    // * Return string replacement for primitive in r
    return typeof objectValue === 'string' ||
      typeof objectValue === 'number' ||
      typeof objectValue === 'boolean' ||
      typeof objectValue === 'bigint'
      ? objectValue.toString()
      : match;
  });

export const flattenObject = (obj: object, keySeparator = '.') => {
  const flattenRecursive = (
    obj: object,
    parentProperty?: string,
    propertyMap: Record<string, unknown> = {},
  ) => {
    for (const [key, value] of Object.entries(obj)) {
      const property = parentProperty
        ? `${parentProperty}${keySeparator}${key}`
        : key;
      if (value && typeof value === 'object') {
        flattenRecursive(value, property, propertyMap);
      } else {
        propertyMap[property] = value;
      }
    }
    return propertyMap;
  };

  return flattenRecursive(obj);
};

export function findLast<T>(
  array: T[],
  predicate: (value: T, index: number, obj: T[]) => boolean,
): T | undefined {
  for (let i = array.length - 1; i >= 0; i--) {
    if (predicate(array[i], i, array)) {
      return array[i];
    }
  }
  return undefined;
}

export async function asyncFilter<T>(
  array: T[],
  predicate: (value: T, index: number, array: T[]) => Promise<boolean>,
): Promise<T[]> {
  const results = await Promise.all(array.map(predicate));
  return array.filter((_, index) => results[index]);
}
